import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { BrandAvatar } from "../auth/BrandAvatar.tsx";
import { useAuth } from "../auth/index.ts";

/**
 * The sidebar footer identity control: a single pill showing the signed-in operator (an initials
 * avatar + their name, with the current team name beneath). Activating it opens a popover with the
 * personal/administrative destinations — Account Management, Teams (superadmin), and Team Members
 * (admin) — replacing the old always-expanded Account section.
 *
 * The popover is a real `<button>`-triggered menu: it closes on outside click, on Escape, and after
 * a selection, and it moves focus to the first item on open (keyboard-operable, design 12.4). The
 * server still enforces every destination regardless of what is shown here.
 */

export function UserMenu(): ReactNode {
  const auth = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);

  const identity = auth.identity;
  const displayName = identity?.user.displayName;
  const email = identity?.user.email ?? "";
  const name = (displayName ?? "").trim() || email || "Signed in";
  const teamName = identity?.team?.name ?? identity?.team?.id ?? "No team selected";
  const isAdmin = identity?.role === "admin";
  const isSuperadmin = identity?.isSuperadmin === true;
  // Seed the abstract avatar on the operator's name (fallback email) so it is stable per person.
  const avatarSeed = (displayName ?? "").trim() || email || "operator";

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Move focus to the first menu item when the popover opens.
  useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  const go = (path: string): void => {
    setOpen(false);
    navigate(path);
  };

  // The destinations, in order; each gated by role. Account is always present.
  const items: { label: string; path: string; show: boolean }[] = [
    { label: "Account Management", path: "/account", show: true },
    { label: "Member Management", path: "/members", show: isAdmin },
    { label: "Teams Management", path: "/teams", show: isSuperadmin },
  ];
  const visible = items.filter((i) => i.show);

  // Log out ends the session server-side (`POST /auth/logout` via the auth context), then sends the
  // operator to the public login route. The context re-derives state from the server, so a failed
  // round-trip leaves the authoritative state intact for the next read rather than a faked local
  // sign-out. We navigate regardless so the operator always lands on /login.
  const onLogout = async (): Promise<void> => {
    setOpen(false);
    try {
      await auth.signOut();
    } finally {
      navigate("/login");
    }
  };

  return (
    <div className="user-menu" ref={rootRef}>
      {open && (
        <div className="user-menu__popover" role="menu" aria-label="Account menu">
          {visible.map((item, idx) => (
            <button
              key={item.path}
              type="button"
              role="menuitem"
              className="user-menu__item"
              onClick={() => go(item.path)}
              ref={idx === 0 ? firstItemRef : undefined}
            >
              {item.label}
            </button>
          ))}
          <div className="user-menu__divider" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="user-menu__item user-menu__item--danger"
            onClick={() => void onLogout()}
            disabled={auth.signingOut}
          >
            {auth.signingOut ? "Logging out\u2026" : "Logout"}
          </button>
        </div>
      )}

      <button
        type="button"
        className="user-menu__pill"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title={name}
      >
        <span className="user-menu__avatar" aria-hidden="true">
          <BrandAvatar seed={avatarSeed} size={32} />
        </span>
        <span className="user-menu__text">
          <span className="user-menu__name">{name}</span>
          <span className="user-menu__team">{teamName}</span>
        </span>
        <svg
          className="user-menu__chevron"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 15l6-6 6 6" />
        </svg>
      </button>
    </div>
  );
}
